import { Fragment, useCallback, useContext, useEffect, useMemo, type MouseEvent, type ReactNode } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { trackClientEvent } from 'analytics/analytics'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LoyaltyOrderViewBenefitsLoungeItem from 'loyalty/components/LoyaltyOrderViewBenefitsLoungeItem'
import LoyaltyOrderViewBenefitsEsimItem from 'loyalty/components/LoyaltyOrderViewBenefitsEsimItem'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import ModalContext from 'contexts/ModalContext'
import RemainingTimeModal from 'components/Insurance/RemainingTimeModal'
import { getRemainingSecondsToPostPurchaseInsuranceBookingProtection } from 'lib/order/orderInsuranceBookingProtectionUtils'
import UpcomingEscapeCommercialRow from './UpcomingEscapeCommercialRow'
import type { UpcomingEscapeUpsellsResult } from './useUpcomingEscapeUpsells'

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

function wrapHeadingPrice(chunks: ReactNode) {
  return (
    <BodyText key="heading-price-wrap" variant="medium" weight="bold" as="span">
      {chunks}
    </BodyText>
  )
}

function getInsuranceDisplayPrice(upsell?: App.InsuranceUpsell): number | undefined {
  if (!upsell) return undefined
  if (
    upsell.checkCanViewLuxPlusBenefits
    && upsell.isEnabledLuxPlusDiscountedInsurance
    && upsell.cheapestQuoteLuxPlusPrice > 0
  ) {
    return upsell.cheapestQuoteLuxPlusPrice
  }
  return upsell.cheapestQuotePrice > 0 ? upsell.cheapestQuotePrice : undefined
}

function formatFromPersonHeading(intl: IntlShape, title: string, amount: number): ReactNode {
  return intl.formatMessage(
    {
      id: 'GlSq9ejGCnSbXhjzk6Wg',
      defaultMessage: '{title}. <price>From {amount}/person</price>',
      description: 'Upcoming Escape commercial row heading with a from-price per person',
    },
    {
      title,
      amount: <FormatCurrency key="heading-price" value={amount} format="roundedDollar" />,
      price: wrapHeadingPrice,
    },
  )
}

function formatFromAdultHeading(intl: IntlShape, title: string, amount: number): ReactNode {
  return intl.formatMessage(
    {
      id: 'OOjkAgDeAWMULTVJkGZS',
      defaultMessage: '{title}. <price>From {amount}/adult</price>',
      description: 'Upcoming Escape commercial row heading with a from-price per adult',
    },
    {
      title,
      amount: <FormatCurrency key="heading-price" value={amount} format="roundedDollar" />,
      price: wrapHeadingPrice,
    },
  )
}

interface TrackedBenefitRowProps {
  analyticsSubject: 'lounge_pass' | 'esim_package'
  children: ReactNode
}

function TrackedBenefitRow({ analyticsSubject, children }: TrackedBenefitRowProps) {
  const [inViewRef, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (!inView) return
    trackClientEvent({
      subject: analyticsSubject,
      action: 'impression',
      category: 'upsell_tile',
      type: 'nonInteraction',
    })
  }, [inView, analyticsSubject])

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const target = event.target
    if (!(target instanceof Element) || !target.closest('a, button')) return
    trackClientEvent({
      subject: analyticsSubject,
      action: 'pressed',
      category: 'upsell_tile',
      type: 'interaction',
    })
  }, [analyticsSubject])

  return <div ref={inViewRef} onClick={handleClick}>{children}</div>
}

interface Props {
  order: App.Order
  upsells: UpcomingEscapeUpsellsResult
}

function UpcomingEscapeUpsellList({ order, upsells }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const {
    eligibility,
    flightUpsell,
    insuranceUpsell,
    insuranceBookingProtectionUpsell,
    carHireUpsell,
    tourUpsell,
    airportTransferUpsell,
    handleFlightUpsellDismiss,
    handleInsuranceUpsellDismiss,
    handleInsuranceBookingProtectionUpsellDismiss,
    handleInsuranceBookingProtectionSelect,
    handleCarHireUpsellDismiss,
    handleTourUpsellDismiss,
    handleAirportTransferDismiss,
    loungeFilters,
  } = upsells

  const {
    showFlight,
    showCarHire,
    showTravelProtection,
    showCancellationProtection,
    showTour,
    showAirportTransfer,
    showLounge,
    showEsim,
  } = eligibility

  const remainingTime = useMemo(() => {
    const totalSeconds = getRemainingSecondsToPostPurchaseInsuranceBookingProtection(order)
    return {
      hours: Math.floor(totalSeconds / 3600),
      minutes: Math.floor(totalSeconds / 60),
    }
  }, [order])

  const cfmrUrgencyLabel = useMemo(() => {
    if (!showCancellationProtection) return undefined
    if (remainingTime.hours >= 1) {
      return intl.formatMessage(
        {
          id: 'jdcNxUgATb3R-x8qnIM7',
          defaultMessage: 'Available for next {hours}h',
          description: 'Alert text showing remaining hours for cancellation protection availability',
        },
        { hours: remainingTime.hours },
      )
    }
    if (remainingTime.minutes > 0) {
      return intl.formatMessage(
        {
          id: '4GhdXnrBTt506DkE5AcZ',
          defaultMessage: 'Available for next {minutes}min',
          description: 'Alert text showing remaining minutes for cancellation protection availability',
        },
        { minutes: remainingTime.minutes },
      )
    }
  }, [intl, remainingTime, showCancellationProtection])

  const handleCfmrUrgencyClick = useCallback(() => {
    showModal(<RemainingTimeModal remainingTime={remainingTime} />)
  }, [remainingTime, showModal])

  const insurancePrice = getInsuranceDisplayPrice(insuranceUpsell)
  const travelProtectionTitle = intl.formatMessage({
    id: 'nUX4LZdR3hOorX4rGEkJ',
    defaultMessage: 'Travel & Cancellation Protection',
    description: 'Homepage Upcoming Escape card heading for the post-purchase travel protection upsell row',
  })
  const travelProtectionHeading = insurancePrice
    ? formatFromPersonHeading(intl, travelProtectionTitle, insurancePrice)
    : travelProtectionTitle

  const cancellationProtectionTitle = intl.formatMessage(bookingProtectionGlossaryMessages.cancellationProtection)
  const cfmrPrice = getInsuranceDisplayPrice(insuranceBookingProtectionUpsell)
  const cancellationProtectionHeading = cfmrPrice
    ? formatFromPersonHeading(intl, cancellationProtectionTitle, cfmrPrice)
    : cancellationProtectionTitle

  const tourTitle = intl.formatMessage({
    id: 'goWtOfIHEDJ9nB7JHhfd',
    defaultMessage: 'More things to do on your tour',
    description: 'Tour optional-experience upsell row label on the homepage Upcoming Escape card',
  })
  const tourHeading = tourUpsell?.price
    ? formatFromPersonHeading(intl, tourTitle, tourUpsell.price)
    : tourTitle

  const transferTitle = intl.formatMessage({
    id: 'QXPriXxX74os6h-BMTFF',
    defaultMessage: 'Airport transfers',
    description: 'Airport transfer upsell row label on the homepage Upcoming Escape card',
  })
  const transferHeading = airportTransferUpsell?.price
    ? formatFromAdultHeading(intl, transferTitle, airportTransferUpsell.price)
    : transferTitle

  const carHireTitle = intl.formatMessage({
    id: '6n7BuqBB3GNZGLH48_9Z',
    defaultMessage: 'Hire a car',
    description: 'Car hire upsell row label on the homepage Upcoming Escape card',
  })
  const carHireHeading = carHireUpsell?.discountPercentage
    ? intl.formatMessage(
        {
          id: 'xT4hWDgIkwraePSVyRsd',
          defaultMessage: 'Hire a car. <price>{discount}% off</price>',
          description: 'Car hire upsell row with bundled discount percentage on the homepage Upcoming Escape card',
        },
        {
          discount: carHireUpsell.discountPercentage,
          price: wrapHeadingPrice,
        },
      )
    : carHireTitle

  let flightHeading: ReactNode | undefined
  if (flightUpsell?.flightTitle) {
    flightHeading = flightUpsell.flightPrice
      ? formatFromAdultHeading(intl, flightUpsell.flightTitle, flightUpsell.flightPrice)
      : flightUpsell.flightTitle
  }

  const rows: Array<{ key: string, node: ReactNode }> = []

  if (showTour && tourUpsell) {
    rows.push({
      key: 'tour',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LineHotAirBalloonIcon}
          heading={tourHeading}
          onClick={tourUpsell.onClick}
          onDismiss={handleTourUpsellDismiss}
          analyticsSubject="tour"
        />
      ),
    })
  }

  if (showFlight && flightHeading && flightUpsell) {
    rows.push({
      key: 'flight',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LinePlaneDepartureIcon}
          heading={flightHeading}
          to={flightUpsell.flightLink}
          onDismiss={handleFlightUpsellDismiss}
          analyticsSubject="flight"
        />
      ),
    })
  }

  if (showTravelProtection && insuranceUpsell) {
    rows.push({
      key: 'insurance',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LineShieldCheckIcon}
          heading={travelProtectionHeading}
          to={`/travel-protection/add?orderId=${insuranceUpsell.orderId}`}
          onDismiss={handleInsuranceUpsellDismiss}
          analyticsSubject="insurance"
        />
      ),
    })
  } else if (showCancellationProtection && insuranceBookingProtectionUpsell) {
    rows.push({
      key: 'cfmr',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LineShieldCheckIcon}
          heading={cancellationProtectionHeading}
          onClick={handleInsuranceBookingProtectionSelect}
          onDismiss={handleInsuranceBookingProtectionUpsellDismiss}
          analyticsSubject="cfmr"
          urgencyLabel={cfmrUrgencyLabel}
          onUrgencyClick={handleCfmrUrgencyClick}
        />
      ),
    })
  }

  if (showAirportTransfer && airportTransferUpsell) {
    rows.push({
      key: 'transfer',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LineTaxiIcon}
          heading={transferHeading}
          to={airportTransferUpsell.experienceSearchUrl}
          onDismiss={handleAirportTransferDismiss}
          analyticsSubject="transfer"
        />
      ),
    })
  }

  if (showCarHire && carHireUpsell) {
    rows.push({
      key: 'car-hire',
      node: (
        <UpcomingEscapeCommercialRow
          icon={LineCarIcon}
          heading={carHireHeading}
          to={carHireUpsell.carHireSearchLink}
          onDismiss={handleCarHireUpsellDismiss}
          analyticsSubject="car_hire"
        />
      ),
    })
  }

  if (showLounge) {
    rows.push({
      key: 'lounge',
      node: (
        <TrackedBenefitRow analyticsSubject="lounge_pass">
          <LoyaltyOrderViewBenefitsLoungeItem
            order={order}
            layout="row"
            availabilityFilters={loungeFilters}
          />
        </TrackedBenefitRow>
      ),
    })
  }

  if (rows.length === 0 && !showEsim) {
    return null
  }

  return (
    <Rows data-testid="upcoming-escape-upsell-rows">
      {rows.map(row => (
        <Fragment key={row.key}>
          <Divider kind="primary" />
          {row.node}
        </Fragment>
      ))}
      {showEsim && (
        <>
          <Divider kind="primary" />
          <TrackedBenefitRow analyticsSubject="esim_package">
            <LoyaltyOrderViewBenefitsEsimItem order={order} layout="row" />
          </TrackedBenefitRow>
        </>
      )}
    </Rows>
  )
}

export default UpcomingEscapeUpsellList
